// src/components/frontpage/SwapCreateModal.jsx
// Modal for creating a new swap listing.
// Session-local only — no database table exists yet.
// On submit, calls onCreateListing() which prepends the item to the Swap column
// via session-local state in LocalityFrontPage. The listing appears immediately.
// Photo uses URL.createObjectURL for local preview (no storage upload).

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../core/Modal';

const SWAP_CATEGORIES = [
  'swap_cat_furniture', 'swap_cat_electronics', 'swap_cat_clothing',
  'swap_cat_garden', 'swap_cat_kids', 'swap_cat_books',
  'swap_cat_sports', 'swap_cat_other'
];

const DURATION_OPTIONS = [7, 14, 30];

const SwapCreateModal = ({ isOpen, onClose, onCreateListing }) => {
  const { t } = useTranslation('frontpage');
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [title, setTitle] = useState('');
  const [subLocality, setSubLocality] = useState('');
  const [duration, setDuration] = useState(14);
  const [category, setCategory] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Revoke previous URL if any
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = () => {
    if (!title.trim()) return;

    const newItem = {
      id: `preview-${Date.now()}`,
      title: title.trim(),
      image_url: imagePreviewUrl || null,
      sub_locality: subLocality.trim() || 'Unknown',
      days_remaining: duration,
      description: '',
      poster_name: currentUser?.display_name || currentUser?.email || 'You',
      isPreview: true
    };

    onCreateListing?.(newItem);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      panelStyle={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '400px',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        fontFamily: 'OpenSauceSans, sans-serif'
      }}
    >
        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', margin: '0 0 16px 0' }}>
          {t('swap_create_title')}
        </h3>

        {/* Title input */}
        <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
          {t('swap_title_label')}
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('swap_title_placeholder')}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            marginBottom: '12px',
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '13px',
            boxSizing: 'border-box'
          }}
        />

        {/* Sub-locality input */}
        <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
          {t('swap_sub_locality_label')}
        </label>
        <input
          type="text"
          value={subLocality}
          onChange={(e) => setSubLocality(e.target.value)}
          placeholder={t('swap_sub_locality_placeholder')}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            marginBottom: '12px',
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '13px',
            boxSizing: 'border-box'
          }}
        />

        {/* Duration select */}
        <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
          {t('swap_duration_label')}
        </label>
        <select
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            marginBottom: '12px',
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '13px',
            boxSizing: 'border-box'
          }}
        >
          {DURATION_OPTIONS.map(d => (
            <option key={d} value={d}>{t(`swap_duration_${d}`)}</option>
          ))}
        </select>

        {/* Category dropdown */}
        <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
          {t('swap_category_label')}
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            marginBottom: '12px',
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '13px',
            boxSizing: 'border-box'
          }}
        >
          <option value="">---</option>
          {SWAP_CATEGORIES.map(cat => (
            <option key={cat} value={cat}>{t(cat)}</option>
          ))}
        </select>

        {/* Photo (optional) — local file select with object URL preview */}
        <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
          {t('swap_photo_label')}
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{
            padding: '8px 16px',
            border: '1px dashed #d1d5db',
            borderRadius: '6px',
            background: imagePreviewUrl ? '#f0f9ff' : 'white',
            cursor: 'pointer',
            marginBottom: '4px',
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '13px'
          }}
        >
          {imageFile ? imageFile.name : t('swap_photo_label')}
        </button>
        {imagePreviewUrl && (
          <div style={{ marginBottom: '12px', marginTop: '4px' }}>
            <img
              src={imagePreviewUrl}
              alt=""
              style={{
                width: '100%',
                height: '120px',
                objectFit: 'cover',
                borderRadius: '6px'
              }}
            />
          </div>
        )}
        {!imagePreviewUrl && <div style={{ marginBottom: '12px' }} />}

        {/* Submit / Cancel */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: 'white',
              cursor: 'pointer',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '13px'
            }}
          >
            {t('swap_cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim()}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              background: title.trim() ? theme.primary : '#9ca3af',
              color: 'white',
              cursor: title.trim() ? 'pointer' : 'not-allowed',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '13px',
              fontWeight: '600'
            }}
          >
            {t('swap_submit')}
          </button>
        </div>
    </Modal>
  );
};

export default SwapCreateModal;
